import { proxy } from 'valtio';

import type { CriticalClearingTimeResult, FuseI2TResult, PeakCurrentResult } from '../helpers/FaultSimulationHelpers';

type CacheEntry<T> = {
    value: T;
    timestamp: number;
};

type FaultSimulationCache = {
    criticalClearingTime: Record<string, CacheEntry<CriticalClearingTimeResult | null>>;
    fusesI2t: Record<string, CacheEntry<FuseI2TResult[]>>;
    fuseI2t: Record<string, CacheEntry<FuseI2TResult | null>>;
    peakCurrents: Record<string, CacheEntry<PeakCurrentResult[]>>;
    componentFaultCurrent: Record<string, CacheEntry<number>>;
    componentTouchVoltage: Record<string, CacheEntry<number | null>>;
};

export const faultSimulationCache = proxy<FaultSimulationCache>({
    criticalClearingTime: {},
    fusesI2t: {},
    fuseI2t: {},
    peakCurrents: {},
    componentFaultCurrent: {},
    componentTouchVoltage: {},
});

const generateCacheKey = (simulationId: string, ...params: (string | number | undefined)[]): string => {
    return [simulationId, ...params.filter((p) => p !== undefined)].join('::');
};

export const FaultSimulationCacheService = {
    getCriticalClearingTime: (simulationId: string): CriticalClearingTimeResult | null | undefined => {
        const key = generateCacheKey(simulationId);
        return faultSimulationCache.criticalClearingTime[key]?.value;
    },

    setCriticalClearingTime: (simulationId: string, value: CriticalClearingTimeResult | null): void => {
        const key = generateCacheKey(simulationId);
        faultSimulationCache.criticalClearingTime[key] = {
            value,
            timestamp: Date.now(),
        };
    },

    getFusesI2t: (simulationId: string): FuseI2TResult[] | undefined => {
        const key = generateCacheKey(simulationId);
        return faultSimulationCache.fusesI2t[key]?.value;
    },

    setFusesI2t: (simulationId: string, value: FuseI2TResult[]): void => {
        const key = generateCacheKey(simulationId);
        faultSimulationCache.fusesI2t[key] = {
            value,
            timestamp: Date.now(),
        };
    },

    getFuseI2t: (simulationId: string, fuseId: string, endTimeIndex?: number): FuseI2TResult | null | undefined => {
        const key = generateCacheKey(simulationId, fuseId, endTimeIndex);
        return faultSimulationCache.fuseI2t[key]?.value;
    },

    setFuseI2t: (
        simulationId: string,
        fuseId: string,
        endTimeIndex: number | undefined,
        value: FuseI2TResult | null,
    ): void => {
        const key = generateCacheKey(simulationId, fuseId, endTimeIndex);
        faultSimulationCache.fuseI2t[key] = {
            value,
            timestamp: Date.now(),
        };
    },

    getPeakCurrents: (simulationId: string): PeakCurrentResult[] | undefined => {
        const key = generateCacheKey(simulationId);
        return faultSimulationCache.peakCurrents[key]?.value;
    },

    setPeakCurrents: (simulationId: string, value: PeakCurrentResult[]): void => {
        const key = generateCacheKey(simulationId);
        faultSimulationCache.peakCurrents[key] = {
            value,
            timestamp: Date.now(),
        };
    },

    getComponentFaultCurrent: (simulationId: string, componentInstanceId: string, port: string): number | undefined => {
        const key = generateCacheKey(simulationId, componentInstanceId, port);
        return faultSimulationCache.componentFaultCurrent[key]?.value;
    },

    setComponentFaultCurrent: (
        simulationId: string,
        componentInstanceId: string,
        port: string,
        value: number,
    ): void => {
        const key = generateCacheKey(simulationId, componentInstanceId, port);
        faultSimulationCache.componentFaultCurrent[key] = {
            value,
            timestamp: Date.now(),
        };
    },

    getComponentTouchVoltage: (simulationId: string, componentInstanceId: string): number | null | undefined => {
        const key = generateCacheKey(simulationId, componentInstanceId);
        return faultSimulationCache.componentTouchVoltage[key]?.value;
    },

    setComponentTouchVoltage: (simulationId: string, componentInstanceId: string, value: number | null): void => {
        const key = generateCacheKey(simulationId, componentInstanceId);
        faultSimulationCache.componentTouchVoltage[key] = {
            value,
            timestamp: Date.now(),
        };
    },

    clearSimulation: (simulationId: string): void => {
        Object.keys(faultSimulationCache.criticalClearingTime).forEach((key) => {
            if (key.startsWith(simulationId)) {
                delete faultSimulationCache.criticalClearingTime[key];
            }
        });

        Object.keys(faultSimulationCache.fusesI2t).forEach((key) => {
            if (key.startsWith(simulationId)) {
                delete faultSimulationCache.fusesI2t[key];
            }
        });

        Object.keys(faultSimulationCache.fuseI2t).forEach((key) => {
            if (key.startsWith(simulationId)) {
                delete faultSimulationCache.fuseI2t[key];
            }
        });

        Object.keys(faultSimulationCache.peakCurrents).forEach((key) => {
            if (key.startsWith(simulationId)) {
                delete faultSimulationCache.peakCurrents[key];
            }
        });

        Object.keys(faultSimulationCache.componentFaultCurrent).forEach((key) => {
            if (key.startsWith(simulationId)) {
                delete faultSimulationCache.componentFaultCurrent[key];
            }
        });

        Object.keys(faultSimulationCache.componentTouchVoltage).forEach((key) => {
            if (key.startsWith(simulationId)) {
                delete faultSimulationCache.componentTouchVoltage[key];
            }
        });
    },

    clearI2t: (): void => {
        faultSimulationCache.fusesI2t = {};
        faultSimulationCache.fuseI2t = {};
    },

    clearAll: (): void => {
        faultSimulationCache.criticalClearingTime = {};
        faultSimulationCache.fusesI2t = {};
        faultSimulationCache.fuseI2t = {};
        faultSimulationCache.peakCurrents = {};
        faultSimulationCache.componentFaultCurrent = {};
        faultSimulationCache.componentTouchVoltage = {};
    },
};
